import React from "react";
import { ownSubTree } from "../../lib/teamTree";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, Edit, KeyRound, LayoutList, ListTree, Plus, Search, Trash2, UserPlus } from "lucide-react";

// Client list with search, notes and goals.
const ClientsTab = ({
  clients,
  collapsedGroups,
  exercises,
  filteredClients,
  openClientEdit,
  removeClient,
  searchClients,
  setAiWorkoutNotes,
  setAiWorkoutOpen,
  setAiWorkoutPlan,
  setCollapsedGroups,
  setPlanOpen,
  setSearchClients,
  connectClientByCode,
  createManagedClient,
  inviteManagedClient,
  plan,
  teamData,
  user,
  clientsView,
  setClientsView,
  clientTrainerFilter,
  setClientTrainerFilter,
  clientLocationFilter,
  setClientLocationFilter,
  clientTrainerOptions,
  firmLocations,
}) => {
  const [clientCode, setClientCode] = React.useState("");
  // Nowa osoba na sali nie ma jeszcze aplikacji i może nigdy jej nie mieć.
  // Trener musi móc zapisać ją od razu, a nie czekać na jej rejestrację.
  const [newClientName, setNewClientName] = React.useState("");
  return (
    <div className="clients-view">
      <div className="view-header">
        <h1>
          Klienci
          {plan?.limits?.max_clients ? (
            <span data-testid="plan-usage"
                  style={{ marginLeft: 10, fontSize: 13, fontWeight: 400, color: "var(--text-secondary)" }}>
              {plan.usage.clients} z {plan.limits.max_clients} w planie darmowym
            </span>
          ) : null}
        </h1>
        <div style={{display:'flex', gap:'8px', alignItems:'center', flexWrap: 'wrap'}}>
          <div style={{position:'relative'}}>
            <Search size={16} style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:'var(--text-secondary)'}} />
            <Input placeholder="Szukaj..." value={searchClients} onChange={e => setSearchClients(e.target.value)} style={{paddingLeft:32,width:200}} />
          </div>
          {clientTrainerOptions.length > 1 && (
            <Select value={clientTrainerFilter || "all"} onValueChange={(v) => setClientTrainerFilter(v === "all" ? "" : v)}>
              <SelectTrigger style={{ width: 160 }} data-testid="client-trainer-filter"><SelectValue placeholder="Trener" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Trener (wszyscy)</SelectItem>
                {clientTrainerOptions.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {(firmLocations || []).length > 0 && (
            <Select value={clientLocationFilter || "all"} onValueChange={(v) => setClientLocationFilter(v === "all" ? "" : v)}>
              <SelectTrigger style={{ width: 170 }} data-testid="client-location-filter"><SelectValue placeholder="Lokalizacja" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Lokalizacja (wszystkie)</SelectItem>
                {firmLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div style={{ display: "flex", border: "1px solid var(--border, rgba(120,120,120,0.2))", borderRadius: 8, overflow: "hidden" }}>
            <button type="button" onClick={() => setClientsView("tree")} title="Drzewko" data-testid="clients-view-tree"
                    style={{ padding: "6px 10px", border: "none", cursor: "pointer",
                             background: clientsView === "tree" ? "var(--primary)" : "transparent",
                             color: clientsView === "tree" ? "#fff" : "var(--text-secondary)" }}>
              <ListTree size={16} />
            </button>
            <button type="button" onClick={() => setClientsView("list")} title="Lista" data-testid="clients-view-list"
                    style={{ padding: "6px 10px", border: "none", cursor: "pointer",
                             background: clientsView === "list" ? "var(--primary)" : "transparent",
                             color: clientsView === "list" ? "#fff" : "var(--text-secondary)" }}>
              <LayoutList size={16} />
            </button>
          </div>
          {/* Podpięcie kodem podopiecznego — do tej pory trener mógł klienta
              tylko usunąć, dodać nie mógł w ogóle. */}
          <Input placeholder="Kod podopiecznego" value={clientCode} data-testid="client-code-input"
                 onChange={(e) => setClientCode(e.target.value.toUpperCase())}
                 onKeyDown={(e) => { if (e.key === "Enter" && clientCode.trim()) { connectClientByCode(clientCode, () => setClientCode("")); } }}
                 style={{ width: 170 }} />
          <Button variant="outline" data-testid="connect-client-btn" disabled={!clientCode.trim()}
                  onClick={() => connectClientByCode(clientCode, () => setClientCode(""))}>
            <UserPlus size={18} /> Podepnij
          </Button>
          <Input placeholder="Imię i nazwisko" value={newClientName} data-testid="new-client-name-input"
                 onChange={(e) => setNewClientName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter" && newClientName.trim()) { createManagedClient(newClientName, () => setNewClientName("")); } }}
                 style={{ width: 170 }} />
          <Button variant="outline" data-testid="add-client-btn" disabled={!newClientName.trim()}
                  onClick={() => createManagedClient(newClientName, () => setNewClientName(""))}>
            <Plus size={18} /> Dodaj
          </Button>
          <Button onClick={() => setPlanOpen(true)} disabled={clients.length === 0 || exercises.length === 0}>
            <Plus size={18} /> Nowy plan
          </Button>
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <Card className="empty-state">
          <CardContent>
            <UserPlus size={48} strokeWidth={1} />
            <h3>Brak klientów</h3>
            <p>Udostępnij kod <strong>{user.trainer_code}</strong> swoim klientom</p>
          </CardContent>
        </Card>
      ) : clientsView === "list" ? (
        // Tabela, nie karty — .trainer-main nie ma cappingu szerokości (w
        // odróżnieniu od .main-content klienta), więc przy 12 trenerach ma
        // gdzie się rozłożyć zamiast wymuszać rozwijanie 12 sekcji drzewka.
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border, rgba(120,120,120,0.2))" }}>
                <th style={{ padding: "8px 10px" }}>Klient</th>
                <th style={{ padding: "8px 10px" }}>Trener(zy)</th>
                <th style={{ padding: "8px 10px" }}>Lokalizacja(e)</th>
                <th style={{ padding: "8px 10px" }}>Ostatni trening</th>
                <th style={{ padding: "8px 10px" }}>Ten tydz.</th>
                <th style={{ padding: "8px 10px" }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.id} className="clickable-card" data-testid={`client-row-${client.id}`}
                    onClick={() => openClientEdit(client)}
                    style={{ borderBottom: "1px solid var(--border, rgba(120,120,120,0.1))", cursor: "pointer" }}>
                  <td style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar className="client-avatar" style={{ width: 28, height: 28 }}>
                      <AvatarFallback>{client.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div style={{ fontWeight: 500 }}>{client.name}</div>
                      <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>{client.email}</div>
                    </div>
                  </td>
                  <td style={{ padding: "8px 10px" }}>
                    {(client.assigned_trainers || []).map(t => t.name).join(", ") || "—"}
                  </td>
                  <td style={{ padding: "8px 10px" }}>
                    {(firmLocations || [])
                      .filter(l => (client.location_ids || []).includes(l.id))
                      .map(l => l.name).join(", ") || "—"}
                  </td>
                  <td style={{ padding: "8px 10px", color: "var(--text-secondary)" }}>
                    {client.last_workout ? new Date(client.last_workout).toLocaleDateString("pl-PL") : "—"}
                  </td>
                  <td style={{ padding: "8px 10px" }}>{client.workouts_this_week || 0}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right" }}>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeClient(client.id); }} data-testid={`remove-client-row-${client.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (() => {
        const renderClientCard = (client) => (
          <Card key={client.id} className="client-card clickable-card" data-testid={`client-${client.id}`} onClick={() => openClientEdit(client)}>
            <CardContent>
              <Avatar className="client-avatar">
                <AvatarFallback>{client.name.charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="client-info">
                <h3>{client.name}</h3>
                <p>{client.email}</p>
              </div>
              <div className="client-stats">
                <div className="stat">
                  <span className="value">{client.workouts_this_week || 0}</span>
                  <span className="label">Ten tydz.</span>
                </div>
                {client.current_weight && (
                  <div className="stat">
                    <span className="value">{client.current_weight}</span>
                    <span className="label">kg</span>
                  </div>
                )}
              </div>
              <div className="client-actions">
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openClientEdit(client); }} data-testid={`edit-client-${client.id}`}>
                  <Edit size={16} />
                </Button>
                {client.is_managed && (
                  <Button variant="ghost" size="sm" title="Kod, którym podopieczny przejmie swój profil"
                          onClick={(e) => { e.stopPropagation(); inviteManagedClient(client); }}
                          data-testid={`invite-client-${client.id}`}>
                    <KeyRound size={16} />
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeClient(client.id); }} data-testid={`remove-client-${client.id}`}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>
        );

        // Build trainer tree structure for client grouping
        const directClients = filteredClients.filter(c => c.is_direct !== false);
        const tree = teamData.tree || [];

        // Recursively render trainer sections with their clients
        const renderTrainerSection = (trainerNode, depth = 0) => {
          const trainerClients = filteredClients.filter(c => c.trainer_id === trainerNode.id);
          const colors = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd'];
          const bgColor = colors[Math.min(depth, colors.length - 1)];
          return (
            <div key={trainerNode.id} className="client-group" style={{marginLeft: depth > 0 ? '20px' : '0', borderLeft: depth > 0 ? '3px solid ' + bgColor : 'none', paddingLeft: depth > 0 ? '16px' : '0'}}>
              <div className="client-group-header clickable-card" style={{borderLeftColor: bgColor}} onClick={() => setCollapsedGroups(prev => ({...prev, [trainerNode.id]: !prev[trainerNode.id]}))}>
                <div className="client-group-avatar" style={{background: bgColor}}>
                  {trainerNode.name?.charAt(0)}
                </div>
                <div>
                  <h3>{trainerNode.name}</h3>
                  <p>{trainerClients.length} {trainerClients.length === 1 ? 'klient' : 'klientów'}</p>
                </div>
                <ChevronDown size={16} className="group-chevron" style={{transform: collapsedGroups[trainerNode.id] ? 'rotate(-90deg)' : 'rotate(0)'}} />
              </div>
              {!collapsedGroups[trainerNode.id] && trainerClients.length > 0 && (
                <div className="clients-list">
                  {trainerClients.map(renderClientCard)}
                </div>
              )}
              {!collapsedGroups[trainerNode.id] && (trainerNode.sub_trainers || []).map(sub => renderTrainerSection(sub, depth + 1))}
            </div>
          );
        };

        return (
          <div className="clients-grouped">
            {/* Own clients */}
            <div className="client-group">
              <div className="client-group-header clickable-card" style={{borderLeftColor: 'var(--primary)'}} onClick={() => setCollapsedGroups(prev => ({...prev, own: !prev.own}))}>
                <div className="client-group-avatar" style={{background: 'var(--primary)'}}>
                  {user.name?.charAt(0)}
                </div>
                <div>
                  <h3>Moi klienci</h3>
                  <p>{directClients.length} {directClients.length === 1 ? 'klient' : 'klientów'}</p>
                </div>
                <ChevronDown size={16} className="group-chevron" style={{transform: collapsedGroups.own ? 'rotate(-90deg)' : 'rotate(0)'}} />
              </div>
              {!collapsedGroups.own && (
                <div className="clients-list">
                  {directClients.map(renderClientCard)}
                </div>
              )}
            </div>

            {/* Sub-trainer sections — only the trainers under the signed-in
                one; see src/lib/teamTree.js for why the raw tree cannot be
                rendered as received. "Moi klienci" above covers their own. */}
            {ownSubTree(tree, user.id).map(node => renderTrainerSection(node, 0))}
          </div>
        );
      })()}
    </div>
  );
};

export default ClientsTab;
